import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    AppBar as MuiAppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    Container,
    Button,
    MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {useTranslation} from "react-i18next";
import Logo from "./Logo";
import {removeJwt} from "utils/jwt";
import {Feature, useAuthorization, useCurrentUser} from "user";
import LanguageSelector from "./LanguageSelector";

const ResponsiveAppBar = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const user = useCurrentUser();
    const [anchorElNav, setAnchorElNav] = useState(null);
    const [anchorElUser, setAnchorElUser] = useState(null);
    const hasAccess = useAuthorization();

    const handleOpenNavMenu = (event: any) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event: any) => {
        setAnchorElUser(event.currentTarget);
    };

    const closeNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const pages = {shops: t("menu.shops")} as { shops: string; customer_journeys?: string; stats?: string; accessibility?: string };
    if (hasAccess(Feature.CustomerJourneys)) pages.customer_journeys = t("menu.customersJourneys");
    if (hasAccess(Feature.Stats)) pages.stats = t("menu.stats");
    if (i18n.language === "fr") pages.accessibility = t("menu.accessibility");

    return (
        <MuiAppBar position="static" sx={{background: "#E5E5E5", boxShadow: "none"}}>
            <Container maxWidth="xl">
                <Toolbar>
                    <Logo
                        variant="h5"
                        sx={{display: {xs: "none", md: "flex", cursor: "pointer"}}}
                        onClick={() => navigate("/")}
                    />
                    <Box component="nav" aria-label={t("a11y.mainNavigation")} sx={{flexGrow: 1}}>
                        <Box sx={{display: {xs: "flex", md: "none"}}}>
                            <IconButton
                                size="large"
                                onClick={handleOpenNavMenu}
                                color="inherit"
                                aria-label={t("a11y.openNavigationMenu")}
                            >
                                <MenuIcon sx={{color: "#000000"}}/>
                            </IconButton>
                            <Menu
                                anchorEl={anchorElNav}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                                open={Boolean(anchorElNav)}
                                onClose={closeNavMenu}
                                sx={{
                                    display: {xs: "block", md: "none"},
                                }}
                            >
                                {Object.entries(pages).map(([path, name]) => (
                                    <MenuItem
                                        key={name}
                                        onClick={() => {
                                            navigate(path);
                                            closeNavMenu();
                                        }}
                                    >
                                        <Typography textAlign="center"
                                                    sx={{color: "#434346", textTransform: "uppercase"}}>
                                            {name}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                        <Box component="ul"
                             sx={{display: {xs: "none", md: "flex"}, listStyle: "none", margin: 0, padding: 0}}>
                            {Object.entries(pages).map(([path, name]) => (
                                <li key={name}>
                                    <Button
                                        key={name}
                                        onClick={closeNavMenu}
                                        sx={{
                                            my: 2,
                                            ml: 8,
                                            display: "block",
                                            color: "#434346",
                                            textTransform: "uppercase",
                                            fontWeight: 700,
                                            fontSize: "16px",
                                        }}
                                        href={path}
                                    >
                                        {name}
                                    </Button>
                                </li>
                            ))}
                        </Box>
                    </Box>
                    <Logo
                        variant="h5"
                        sx={{display: {xs: "flex", md: "none", cursor: "pointer", flexGrow: 1}}}
                        onClick={() => navigate("/")}
                    />
                    <Box sx={{flexGrow: 0}}>
                        <LanguageSelector/>
                    </Box>
                    <Box sx={{flexGrow: 0}}>
                        <IconButton onClick={handleOpenUserMenu} sx={{p: 0, display: "block"}}
                                    aria-label={t("a11y.openUserMenu")}>
                            <Typography fontWeight={600} fontSize="24px" lineHeight="19px">
                                {user.roleDisplay}
                            </Typography>
                            <Typography fontWeight={300} fontSize="15px" lineHeight="12px" textTransform="uppercase">
                                {user.nickname}
                            </Typography>
                        </IconButton>
                        <Menu
                            sx={{mt: "45px"}}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            <MenuItem
                                key="logout"
                                onClick={() => {
                                    removeJwt();
                                    window.location.replace("/");
                                }}
                            >
                                <Typography textAlign="center">{t("logout")}</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </MuiAppBar>
    );
};
export default ResponsiveAppBar;
